import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

// Avoid lazy loading ProtectedRoute to prevent delays in authentication checks (e.g., checkSession)
import ProtectedRoute from './components/HOC/ProtectedRoute';
import { pagesMap } from './constants';

const HomePageView = lazy(() => import('./pages/Home/HomePageView'));
const RouteErrorPageView = lazy(() => import('./pages/RouteError/RouteErrorPageView'));
const EntityEditView = lazy(() => import('./pages/EntityEdit/EntityEditView'));
const AssetsManagerView = lazy(() => import('./pages/AssetsManager/AssetsManagerView'));
const ProjectMainPage = lazy(() => import('./pages/Projects/Stepper/MainPage/MainPage'));
const CreateProjectPage = lazy(() => import('./pages/Projects/CreateProjectPage/CreateProjectPage'));
const FolderFormView = lazy(() => import('./pages/FolderForm/FolderFormView'));
const LogoutView = lazy(() => import('./pages/Logout/LogoutView'));
const GroupsListView = lazy(() => import('./pages/Admin/Groups/GroupsListView'));
const GroupEditView = lazy(() => import('./pages/Admin/Groups/GroupEditView'));
const MyWorkspaceView = lazy(() => import('./pages/MyWorkspace/MyWorkspaceView'));
const WorkspaceGQLPage = lazy(() => import('./pages/Workspaces/WorkspaceGQLPage'));
const WorkspaceTemplatesView = lazy(() => import('./pages/Workspaces/Templates/WorkspaceTemplatesListView'));
const WorkspaceTemplatesEditView = lazy(() => import('./pages/Workspaces/Templates/WorkspaceTemplateEditView'));
const ImpersonateView = lazy(() => import('./pages/Impersonate/ImpersonateView'));

const AssetManagerLayout = lazy(() => import('./layouts/AssetManagerLayout/AssetManagerLayout'));
const UserProfileLayout = lazy(() => import('./layouts/UserProfileLayout/UserProfileLayout'));
const UserProfileAboutMeView = lazy(() => import('./pages/UserProfile/AboutMeView'));
const UserProfileRolesView = lazy(() => import('./pages/UserProfile/RolesView'));
const UserProfileGroupView = lazy(() => import('./pages/UserProfile/GroupView'));
const UserProfileGrantsView = lazy(() => import('./pages/UserProfile/GrantsView'));

const EntitiesLayout = lazy(() => import('./layouts/EntitiesLayout/EntitesLayout'));
const EntitiesPage = lazy(() => import('./subPages/Entities/EntitiesPage'));
const DocumentsPage = lazy(() => import('./subPages/Documents/DocumentsPage'));

const ReportsLayout = lazy(() => import('./layouts/ReportsLayout/ReportsLayout'));
const DocumentsGeneratedView = lazy(() => import('./pages/Reports/DocumentsGenerated/DocumentsGeneratedView'));
const FilesDownloadedView = lazy(() => import('./pages/Reports/FilesDownloaded/FilesDownloadedView'));

const PresentationEditView = lazy(() => import('./pages/Presentations/EditPresentationView'));
const ConversationsView = lazy(() => import('./pages/Conversations/ConversationsView'));

const PROJECTS_PATH = '/my-workspace/projects';

export const routesNames = {
  home: '/home',

  createProject: `${PROJECTS_PATH}/create`,
  assets: (slug: string) => `/${slug}`,
  folders: (slug: string, folderId: string) => `/${slug}/folders/${folderId}`,
  createFolder: (slug: string, folderId: string) => `/${slug}/folders/${folderId}/create`, // slug -> rootFolder slug, folderId -> parent folderId
  entityEdit: (slug: string, entityType: string, entityId?: string) => `/${slug}/${entityType}/${entityId ?? ''}`,
  editProject: `${PROJECTS_PATH}/:projectId/:slug?`,
  groupsList: '/groups',
  createGroup: '/groups/create',
  workspaceGQL: `/workspaces/:id/graphql`,
  editGroup: (groupId: string) => `/groups/${groupId}/edit`,
  myWorkspace: (id?: string) => `my-workspace${id ? '/' + id : ''}`,
  workspaceTemplates: `${pagesMap.workspaceTemplates.slug}`,
  workspaceTemplatesEdit: `/${pagesMap.workspaceTemplates.slug}/:projectId/:slug?/:itemId?`,
  workspaceTemplateDocumentTemplateCreate: `/${pagesMap.workspaceTemplates.slug}/:projectId/:slug/create`,
  logout: `/logout`,

  entities: '/entities',
  entitiesManage: 'manage',
  entitiesDataUploaded: 'data-uploaded',

  reports: '/reports',
  reportsDocumentsGenerated: 'documents-generated',
  reportsFilesDownloaded: 'files-downloaded',

  presentationsEdit: '/presentations/:presentationId',
  presentationsEditWithFolder: '/presentations/:presentationId/folders/:folderId?',
  presentationsEditSearch: '/presentations/:presentationId/search',

  userProfile: '/profile',
  userProfileAboutMe: 'about-me',
  userProfileGroups: 'groups',
  userProfileRoles: 'roles',
  userProfileGrants: 'grants',

  impersonate: '/impersonate',

  conversationEdit: '/presentations/:presentationId/conversation',
};

const router = createBrowserRouter([
  {
    path: routesNames.logout,
    element: <LogoutView />,
    errorElement: <RouteErrorPageView />,
  },
  {
    path: '/',
    element: <AssetManagerLayout />,
    children: [
      {
        index: true,
        element: <Navigate to={routesNames.home} />,
      },
      {
        path: routesNames.home,
        element: <ProtectedRoute element={<HomePageView />} />,
      },
      {
        path: routesNames.userProfile,
        element: <ProtectedRoute element={<UserProfileLayout />} />,
        errorElement: <RouteErrorPageView />,
        children: [
          {
            path: routesNames.userProfileAboutMe,
            element: <UserProfileAboutMeView />,
          },
          {
            path: routesNames.userProfileRoles,
            element: <UserProfileRolesView />,
          },
          {
            path: routesNames.userProfileGroups,
            element: <UserProfileGroupView />,
          },
          {
            path: routesNames.userProfileGrants,
            element: <UserProfileGrantsView />,
          },
        ],
      },
      {
        path: routesNames.reports,
        element: <ProtectedRoute element={<ReportsLayout />} />,
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            element: <Navigate to={routesNames.reportsDocumentsGenerated} replace />,
          },
          {
            path: routesNames.reportsDocumentsGenerated,
            element: <DocumentsGeneratedView />,
          },
          {
            path: routesNames.reportsFilesDownloaded,
            element: <FilesDownloadedView />,
          },
        ],
      },
      {
        path: routesNames.presentationsEdit,
        element: <ProtectedRoute element={<PresentationEditView />} />,
      },
      {
        path: routesNames.presentationsEditWithFolder,
        element: <ProtectedRoute element={<PresentationEditView />} />,
      },
      {
        path: routesNames.presentationsEditSearch,
        element: <ProtectedRoute element={<PresentationEditView />} />,
      },
      {
        path: routesNames.entities,
        element: <ProtectedRoute element={<EntitiesLayout />} />,
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            element: <Navigate to={routesNames.entitiesManage} replace />,
          },
          {
            path: routesNames.entitiesManage,
            element: <EntitiesPage isGlobal />,
          },
          {
            path: routesNames.entitiesDataUploaded,
            element: <DocumentsPage />,
          },
        ],
      },
      {
        path: routesNames.conversationEdit,
        element: <ProtectedRoute element={<ConversationsView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.impersonate,
        element: <ProtectedRoute element={<ImpersonateView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.groupsList,
        element: <ProtectedRoute element={<GroupsListView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: `/my-workspace/:id?`,
        element: <ProtectedRoute element={<MyWorkspaceView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceTemplates,
        element: <ProtectedRoute element={<WorkspaceTemplatesView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceTemplatesEdit,
        element: <ProtectedRoute element={<WorkspaceTemplatesEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceTemplateDocumentTemplateCreate,
        element: <ProtectedRoute element={<WorkspaceTemplatesEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceGQL,
        element: <ProtectedRoute element={<WorkspaceGQLPage />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.createGroup,
        element: <ProtectedRoute element={<GroupEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.editGroup(':groupId'),
        element: <ProtectedRoute element={<GroupEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.assets(':slug'),
        element: <ProtectedRoute element={<AssetsManagerView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.folders(':slug', ':folderId'),
        element: <ProtectedRoute element={<AssetsManagerView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.createFolder(':slug', ':folderId'),
        element: <ProtectedRoute element={<FolderFormView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.entityEdit(':slug', ':entityType', ':entityId?'),
        element: <ProtectedRoute element={<EntityEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.editProject,
        element: <ProtectedRoute element={<ProjectMainPage />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.createProject,
        element: <ProtectedRoute element={<CreateProjectPage />} />,
        errorElement: <RouteErrorPageView />,
      },
    ],
  },
]);

export const Routes = () => <RouterProvider router={router} />;
